<template>
  <!-- "What do Sam's stars say about us?", run inside the chat. Asks for the
       recipient's birth moment inline only if it's missing (offering the
       sender's guess at her birthday first), then shows the reading — or, when
       the sender has no chart yet, her own signs and a word that he was asked.
       Integration: <OurStars :inv="giftInviteToken" :with-name="senderName" />
       in a chat bubble; it emits `status` (needs_my_birth | waiting_for_sender |
       ready) each time that changes, and `done` with the final payload. -->
  <div class="os" data-testid="our-stars">
    <p v-if="phase === 'loading'" class="os-dim">Reading the two skies…</p>

    <template v-else-if="phase === 'suggested'">
      <SuggestedBirthday :inv="inv" @confirmed="onConfirmed" @declined="phase = 'ask'" @none="phase = 'ask'" />
    </template>

    <template v-else-if="phase === 'ask'">
      <!-- The date only (R6.5, R6.7): a time and place can come later, from
           the closer look inside the report. Not a form up front. -->
      <p class="os-lead">{{ askLead }}</p>
      <BirthMoment mode="inline" :fields="['date']" :initial="myMoment" :saving="saving" :server-errors="errors"
                   submit-label="Read our stars" @save="saveMine" />
    </template>

    <div v-else-if="phase === 'waiting'" class="os-card" data-testid="our-stars-waiting">
      <p v-if="mySigns" class="os-signs">{{ mySigns }}</p>
      <p class="os-text">I've asked {{ withName }} for their birthday. When they add it, you'll both get your reading.</p>
    </div>

    <div v-else-if="phase === 'ready' && reading" class="os-card" data-testid="our-stars-reading">
      <h4 class="os-title">You &amp; {{ reading.with_name }}</h4>
      <ul v-if="meters.length" class="os-meters">
        <li v-for="m in meters" :key="m.key">
          <span class="os-meter-label">{{ m.label }}</span>
          <span class="os-meter"><span class="os-meter-fill" :style="{ width: m.value + '%' }"></span></span>
        </li>
      </ul>
      <p class="os-text" data-testid="our-stars-report">{{ reading.report }}</p>
      <!-- R6.8: from a date, said plainly, with the one closer-look line. -->
      <template v-if="reading.time_warning">
        <template v-if="!reading.my_moment_known">
          <p class="os-dim" data-testid="our-stars-date-sees">{{ DATE_SEES }}</p>
          <p class="os-closer">
            <button type="button" class="os-closer-link" data-testid="closer-look" @click="openCloser">{{ CLOSER_LOOK }}</button>
          </p>
        </template>
        <p v-else-if="!reading.their_moment_known" class="os-dim" data-testid="our-stars-their-time">{{ theirTimeLine(reading.with_name) }}</p>
      </template>
      <p v-if="reading.mystic" class="os-dim">{{ reading.mystic.name }}</p>
    </div>

    <!-- The closer look: time and place only (R6.8); the report is made again (R6.9). -->
    <BirthMoment v-if="closerOpen" mode="modal" :fields="['time', 'place']" :optional="['time', 'place']" :initial="myMoment"
                 title="A closer look" :with-name="withName" :saving="saving" :server-errors="errors"
                 @save="saveCloser" @close="closerOpen = false" />

    <p v-else-if="phase === 'error'" class="os-err">The stars are clouded right now. Try again in a moment.</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import BirthMoment from '@/components/widgets/BirthMoment.vue'
import SuggestedBirthday from '@/components/widgets/SuggestedBirthday.vue'
import { getBirthMoment, ourStars, saveBirthMoment } from '@/api-service'
import { signsLine } from '@/utils/birth-moment'
import { CLOSER_LOOK, DATE_SEES, theirTimeLine } from '@/utils/stage-three'

const props = defineProps({
  inv: { type: String, required: true },
  withName: { type: String, default: 'them' },
})
const emit = defineEmits(['status', 'done'])

const phase = ref('loading')
const reading = ref(null)
const mySigns = ref('')
const myMoment = ref(null)
const saving = ref(false)
const errors = ref(null)
const closerOpen = ref(false)

// "To read your stars with Sam's…"; with no name to give, never "them's"
// (gift bug walk 2026-10-25 #14).
const askLead = computed(() => {
  const n = String(props.withName || '').trim()
  return n && n !== 'them' ? `To read your stars with ${n}'s, I need your birthday.` : 'To read our stars, I need your birthday.'
})
const meters = computed(() => {
  const r = reading.value
  if (!r || !r.meters) return []
  const labels = r.meter_labels || {}
  return Object.entries(r.meters).map(([key, value]) => ({ key, value: Math.max(0, Math.min(100, Number(value) || 0)),
    label: (labels[key] && (labels[key].label || labels[key])) || key }))
})

async function run() {
  phase.value = 'loading'
  try {
    const r = await ourStars(props.inv)
    emit('status', r.status)
    if (r.status === 'needs_my_birth') {
      phase.value = myMoment.value ? 'ask' : 'suggested'
    } else if (r.status === 'waiting_for_sender') {
      mySigns.value = signsLine(r.my_signs)
      phase.value = 'waiting'
      emit('done', r)
    } else if (r.status === 'ready') {
      reading.value = r.reading
      phase.value = 'ready'
      emit('done', r)
    } else {
      phase.value = 'error'
    }
  } catch (e) {
    phase.value = 'error'
  }
}

function onConfirmed(moment) {
  // Her date is set: You & them runs from the date alone (R6.7). The time and
  // place are hers to add from the closer look inside the report.
  myMoment.value = moment || null
  run()
}

async function openCloser() {
  errors.value = null
  if (!myMoment.value || !myMoment.value.birthdate) {
    try { const r = await getBirthMoment(); myMoment.value = (r && r.moment) || myMoment.value } catch (e) { /* the form asks */ }
  }
  closerOpen.value = true
}

async function saveCloser(out) {
  saving.value = true; errors.value = null
  try {
    const birthdate = (myMoment.value && myMoment.value.birthdate) || out.birthdate
    // Either one alone is fine; neither is nothing to save.
    if (!out.time && !out.place) { closerOpen.value = false; return }
    const r = await saveBirthMoment({ birthdate, time: out.time, ...(out.place ? { place: out.place } : {}) })
    myMoment.value = (r && r.moment) || myMoment.value
    closerOpen.value = false
    // Her moment moved: the server made the two of you again (R6.9).
    await run()
  } catch (e) {
    errors.value = (e && e.response && e.response.data && e.response.data.errors) || { _: 'That did not save. Try again.' }
  } finally { saving.value = false }
}

async function saveMine(out) {
  saving.value = true; errors.value = null
  try {
    const r = await saveBirthMoment({ birthdate: out.birthdate, time: out.time, ...(out.place ? { place: out.place } : {}) })
    myMoment.value = r.moment
    await run()
  } catch (e) {
    errors.value = (e && e.response && e.response.data && e.response.data.errors) || { _: 'That did not save. Try again.' }
  } finally { saving.value = false }
}

onMounted(run)
defineExpose({ run })
</script>

<style scoped>
.os { color: #e9dcff; }
.os-lead { margin: 0 0 10px; font-size: 14.5px; }
.os-card { background: rgba(20,7,31,0.92); border: 1px solid rgba(240,217,163,0.22); border-radius: 14px; padding: 16px; }
.os-title { font-family: 'Cinzel', serif; color: #f5e8c0; margin: 0 0 10px; font-size: 16px; }
.os-signs { font-family: 'Cinzel', serif; color: #f0d9a3; font-size: 13px; margin: 0 0 8px; }
.os-text { margin: 0; font-size: 14.5px; line-height: 1.55; }
.os-dim { color: #9b86c9; font-size: 12.5px; margin: 8px 0 0; }
.os-err { color: #f0a3a3; font-size: 13px; }
.os-closer { margin: 8px 0 0; }
.os-closer-link { background: none; border: none; padding: 0; color: #f0d9a3; font: inherit; font-size: 13.5px;
  text-decoration: underline; text-underline-offset: 3px; cursor: pointer; text-align: left; }
.os-meters { list-style: none; padding: 0; margin: 0 0 12px; display: grid; gap: 6px; }
.os-meters li { display: grid; grid-template-columns: 96px 1fr; align-items: center; gap: 8px; font-size: 12px; }
.os-meter-label { color: #9b86c9; text-transform: capitalize; }
.os-meter { height: 6px; background: rgba(255,255,255,0.08); border-radius: 999px; overflow: hidden; }
.os-meter-fill { display: block; height: 100%; background: linear-gradient(90deg, #7b4bb0, #f0d9a3); }
</style>
